/**
 * types/core.ts
 * Entidades operacionales actuales — lo que existe en el schema Supabase HOY.
 * No mezclar con tipos futuros judiciales.
 */

// ─── Enums de negocio ─────────────────────────────────────────────────────────

export type UserRole = 'admin' | 'supervisor' | 'agente' | 'agente_legal'

export type ClientStatus =
  | 'pendiente'
  | 'con_credito'
  | 'sin_credito'
  | 'listo_cobro'
  | 'no_listo'
  | 'lse'
  | 'cerrado_administracion'
  | 'para_impugnar'
  | 'para_embargo'
  | 'seguimiento'

// Labels en español para UI — fuente de verdad única
export const CLIENT_STATUS_LABELS: Record<ClientStatus, string> = {
  pendiente:              'Pendiente',
  con_credito:            'Con Crédito',
  sin_credito:            'Sin Crédito',
  listo_cobro:            'Listo Cobro',
  no_listo:               'No Listo',
  lse:                    'LSE',
  cerrado_administracion: 'Cerrado Adm.',
  para_impugnar:          'Para Impugnar',
  para_embargo:           'Para Embargo',
  seguimiento:            'Seguimiento',
}

// ═══════════════════════════════════════════════════════════════════
// CONVENCIÓN DE COLORES OFICIAL — NO MODIFICAR SIN AUTORIZACIÓN
// ─────────────────────────────────────────────────────────────────
// Pendiente/LSE   → GRIS    #9b9bad
// Con Crédito     → AZUL    #2d5eff  ← listo con crédito aprobado
// Sin Crédito     → VERDE   #16a34a  ← listo pero sin crédito
// No Listo        → GRIS OSCURO — revisado y marcado como no listo
// Para Impugnar   → MORADO  #7c3aed  ← caso legal
// Seguimiento     → NARANJA #d97706  ← necesita seguimiento
// Para Embargo    → ROJO    #dc2626  ← escalado legalmente
// Listo Cobro     → CIAN    — listo para gestión de cobro
// ═══════════════════════════════════════════════════════════════════
export const CLIENT_STATUS_COLORS: Record<ClientStatus, { text: string; bg: string; border: string }> = {
  pendiente:              { text: '#9b9bad', bg: 'rgba(110,110,133,0.12)', border: 'rgba(110,110,133,0.25)' }, // GRIS
  con_credito:            { text: '#2d5eff', bg: 'rgba(45,94,255,0.12)',   border: 'rgba(45,94,255,0.28)'   }, // AZUL
  sin_credito:            { text: '#16a34a', bg: 'rgba(22,163,74,0.12)',   border: 'rgba(22,163,74,0.28)'   }, // VERDE
  listo_cobro:            { text: '#0891b2', bg: 'rgba(8,145,178,0.12)',   border: 'rgba(8,145,178,0.28)'   }, // CIAN
  no_listo:               { text: '#64748b', bg: 'rgba(100,116,139,0.14)', border: 'rgba(100,116,139,0.30)' }, // SLATE — revisado, no listo
  lse:                    { text: '#9b9bad', bg: 'rgba(110,110,133,0.10)', border: 'rgba(110,110,133,0.22)' }, // GRIS
  cerrado_administracion: { text: '#6e6e85', bg: 'rgba(110,110,133,0.10)', border: 'rgba(110,110,133,0.20)' }, // GRIS
  para_impugnar:          { text: '#7c3aed', bg: 'rgba(124,58,237,0.12)',  border: 'rgba(124,58,237,0.28)'  }, // MORADO
  para_embargo:           { text: '#dc2626', bg: 'rgba(220,38,38,0.12)',   border: 'rgba(220,38,38,0.28)'   }, // ROJO
  seguimiento:            { text: '#d97706', bg: 'rgba(217,119,6,0.12)',   border: 'rgba(217,119,6,0.28)'   }, // NARANJA
}

// ─── Entidades de base de datos ───────────────────────────────────────────────

export interface Profile {
  id: string
  full_name: string | null
  username: string
  role: UserRole
  active: boolean
  must_change_password: boolean
  sip_extension: string | null
  created_at: string | null
  updated_at: string | null
}

export interface Campaign {
  id: string
  name: string
  description: string | null
  type: string | null
  status: string | null
  active: boolean | null
  created_by: string | null
  created_at: string | null
  started_at: string | null
  closed_at: string | null
  total_assigned: number | null
  total_completed: number | null
  rango_desde: number | null
  rango_hasta: number | null
  assigned_agents: string[] | null
  config: Record<string, unknown> | null
  metadata: Record<string, unknown> | null
}

export interface Client {
  id: string
  idx: number | null
  numero_cliente: number
  planes: string | null
  tipo_cartera: string
  cedula: string | null
  nombre: string | null
  telefono: string | null
  current_status: ClientStatus | null
  current_campaign_id: string | null
  assigned_to: string | null
  status_updated_at: string | null
  created_at: string | null
  updated_at: string | null
}

export interface ClientStatusHistory {
  id: string
  client_id: string
  previous_status: ClientStatus | null
  new_status: ClientStatus
  changed_by: string | null
  note: string | null
  created_at: string | null
}

export interface ClientNote {
  id: string
  client_id: string
  created_by: string | null
  content: string
  created_at: string | null
}

export interface ClientAssignment {
  id: string
  campaign_id: string
  client_id: string
  assigned_to: string
  assigned_by: string | null
  assigned_at: string | null
  completed: boolean | null
  completed_at: string | null
  status: ClientStatus | null
  last_action_at: string | null
  notes: string | null
}

export interface Impugnacion {
  id: string
  client_id: string
  impugnado: boolean | null
  impugnado_at: string | null
  impugnado_by: string | null
  notes: string | null
  created_at: string | null
}

export interface ActiveClientSession {
  id: string
  client_id: string
  user_id: string
  status: string | null
  opened_at: string | null
  last_activity_at: string | null
}

// ─── Campaign types ───────────────────────────────────────────────────────────

export type CampaignType = 'marcado' | 'impugnacion' | 'cobros' | 'habeas_data' | 'embargo'
export type CampaignStatus = 'activa' | 'pausada' | 'cerrada'

export const CAMPAIGN_TYPE_LABELS: Record<CampaignType, string> = {
  marcado:      'Marcado Listos y No Listos',
  impugnacion:  'Impugnación',
  cobros:       'Cobros / Recuperación',
  habeas_data:  'Habeas Data',
  embargo:      'Embargo',
}

export interface CampaignExtended {
  id: string
  name: string
  description: string | null
  type: CampaignType
  status: CampaignStatus
  active: boolean | null
  config: Record<string, unknown>
  total_assigned: number
  total_completed: number
  created_by: string | null
  started_at: string | null
  closed_at: string | null
  created_at: string | null
}

// ─── Impugnacion estados ──────────────────────────────────────────────────────

export type ImpugnacionEstado =
  | 'pendiente'
  | 'en_proceso'
  | 'documentacion'
  | 'enviado_tribunal'
  | 'resuelto'
  | 'rechazado'

export const IMPUGNACION_ESTADO_LABELS: Record<ImpugnacionEstado, string> = {
  pendiente:         'Pendiente',
  en_proceso:        'En Proceso',
  documentacion:     'Documentación',
  enviado_tribunal:  'Enviado Tribunal',
  resuelto:          'Resuelto',
  rechazado:         'Rechazado',
}

export interface ImpugnacionExtended {
  id: string
  client_id: string
  campaign_id?: string | null
  estado: ImpugnacionEstado
  assigned_to: string | null
  expediente_no: string | null
  documentos: unknown[]
  resultado: string | null
  resuelto_at: string | null
  resuelto_by: string | null
  impugnado: boolean | null
  impugnado_at: string | null
  impugnado_by: string | null
  notes: string | null
  updated_at: string | null
  created_at: string | null
}

// ─── Cobros ───────────────────────────────────────────────────────────────────

export type CobroEstado = 'pendiente' | 'contactado' | 'promesa' | 'pagado' | 'incobrable' | 'embargo'

export const COBRO_ESTADO_LABELS: Record<CobroEstado, string> = {
  pendiente:   'Sin contactar',
  contactado:  'Contactado',
  promesa:     'Con Promesa',
  pagado:      'Pagado',
  incobrable:  'Incobrable',
  embargo:     '→ Embargo',
}

export const COBRO_ESTADO_COLORS: Record<CobroEstado, { text: string; bg: string }> = {
  pendiente:   { text: '#9b9bad', bg: 'rgba(110,110,133,.12)' },
  contactado:  { text: '#0ea5e9', bg: 'rgba(14,165,233,.12)' },
  promesa:     { text: '#d97706', bg: 'rgba(215,119,6,.12)' },
  pagado:      { text: '#16a34a', bg: 'rgba(22,163,74,.12)' },
  incobrable:  { text: '#dc2626', bg: 'rgba(220,38,38,.12)' },
  embargo:     { text: '#dc2626', bg: 'rgba(220,38,38,.18)' },
}

export interface Cobro {
  id: string
  client_id: string
  campaign_id: string | null
  assigned_to: string | null
  estado: CobroEstado
  banco: string | null
  monto_deuda: number | null
  monto_pagado: number | null
  fecha_promesa: string | null
  fecha_pago: string | null
  notas: string | null
  created_by: string | null
  created_at: string | null
  updated_at: string | null
}

// ─── Habeas Data ──────────────────────────────────────────────────────────────

export type HabeasEstado =
  | 'pendiente' | 'consentimiento' | 'expediente'
  | 'audiencia_pendiente' | 'audiencia_programada'
  | 'celebrada' | 'transunion' | 'completado' | 'cancelado'

export const HABEAS_ESTADO_LABELS: Record<HabeasEstado, string> = {
  pendiente:             'Pendiente',
  consentimiento:        'Consentimiento',
  expediente:            'Expediente Registrado',
  audiencia_pendiente:   'Espera Audiencia',
  audiencia_programada:  'Audiencia Programada',
  celebrada:             'Audiencia Celebrada',
  transunion:            'Notif. TransUnion',
  completado:            'Completado',
  cancelado:             'Cancelado',
}

export interface HabeasData {
  id: string
  client_id: string
  campaign_id: string | null
  assigned_to: string | null
  estado: HabeasEstado
  consentimiento_firmado: boolean
  consentimiento_at: string | null
  expediente_no: string | null
  entidad_contra: string | null
  sala: string | null
  audiencia_fecha: string | null
  audiencia_hora: string | null
  resultado: string | null
  notificado_transunion: boolean
  banco_confirmacion: string | null
  notas: string | null
  created_at: string | null
  updated_at: string | null
}

// ─── Locking ──────────────────────────────────────────────────────────────────

export interface ClientLock {
  assignment_id: string
  client_id: string
  locked_by: string
  locked_at: string
  lock_expires_at: string
}
